import { ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '../assets/icons/close.svg?react';
import { useMobileFilter } from '../contexts/MobileFilterContext';
import { useResponsive } from '../hooks/useResponsive';
import { IconButton } from './IconButton';

interface FiltersMobileProps {
  children: ReactNode;
  onApply?: () => void;
  onClear?: () => void;
  hasActiveFilters?: boolean;
}

export function FiltersMobile({
  children,
  onApply,
  onClear,
  hasActiveFilters = false,
}: FiltersMobileProps) {
  const { isOpen, close, toggle } = useMobileFilter();
  const { prefersMobileLayout } = useResponsive();
  const [shouldRender, setShouldRender] = useState(false);

  if (!prefersMobileLayout) return null;

  // Keep component mounted if opening or if we need to animate out
  const show = isOpen || shouldRender;

  const handleAnimationEnd = () => {
    if (!isOpen) {
      setShouldRender(false);
    }
  };

  // Show immediately when opening
  if (isOpen && !shouldRender) {
    setShouldRender(true);
  }

  const handleApply = () => {
    onApply?.();
    close();
  };

  if (!show) return null;

  const state = isOpen ? 'open' : 'closed';

  return createPortal(
    <>
      {/* Overlay */}
      <div
        data-mobile-filter-overlay
        data-state={state}
        className='fixed inset-0 z-40 bg-black/50'
        onClick={close}
        onAnimationEnd={handleAnimationEnd}
      />

      {/* Filter Panel - Full width on mobile */}
      <aside
        data-mobile-filter
        data-state={state}
        className='bg-surface-primary-support border-border-primary fixed top-0
          right-0 z-50 flex h-full w-full flex-col border-l shadow-xl'
      >
        <div
          className='bg-brand-primary flex h-(--spacing-header-height-mobile)
            items-center justify-between px-4'
        >
          <h2 className='text-h5 font-semibold text-white'>Filters</h2>
          <IconButton
            aria-label='Close filters'
            icon={
              <CloseIcon
                height={20}
                width={20}
                className='fill-white [&_path]:fill-white'
              />
            }
            onClick={toggle}
            className='hover:bg-transparent'
          />
        </div>
        <div className='flex flex-1 flex-col gap-4 overflow-y-auto p-4'>
          {children}
        </div>

        {/* Footer with action buttons */}
        <div className='flex flex-col gap-2 p-4'>
          {hasActiveFilters && (
            <button
              onClick={onClear}
              className='text-focus hover:bg-surface-primary-support w-full
                rounded-lg px-4 py-2 text-center font-medium transition-colors'
            >
              Clear Filters
            </button>
          )}
          <button
            onClick={handleApply}
            className='bg-focus hover:bg-focus-hover w-full rounded-lg px-4 py-2
              text-center font-medium text-white transition-colors'
          >
            Apply Filters
          </button>
        </div>
      </aside>
    </>,
    document.body
  );
}
